/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "@app/axios/axiosIns";

const pickFilenameFromContentDisposition = (value?: string): string | undefined => {
  if (!value) return undefined;

  const match =
    value.match(/filename\*=\s*UTF-8''([^;]+)/i) ||
    value.match(/filename\s*=\s*"([^"]+)"/i) ||
    value.match(/filename\s*=\s*([^;]+)/i);

  const raw = match?.[1]?.trim();
  if (!raw) return undefined;

  try {
    return decodeURIComponent(raw.replace(/^["']|["']$/g, ""));
  } catch {
    return raw.replace(/^["']|["']$/g, "");
  }
};

export const exportContactUs = createAsyncThunk(
  "contactUs/export/data",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/admin/contact-us/export/data", {
        responseType: "blob",
      });

      const headers = response?.headers || {};
      const contentDisposition: string | undefined =
        headers["content-disposition"] || headers["Content-Disposition"];

      return {
        blob: response?.data as Blob,
        filename:
          pickFilenameFromContentDisposition(contentDisposition) ||
          "contact_us_export.csv",
        contentType:
          (headers["content-type"] || headers["Content-Type"]) as string | undefined,
      };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

export const addContactUs = createAsyncThunk(
  "contactUs/add",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/contact-us", data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getAllContactUs = createAsyncThunk(
  "contactUs/all",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/admin/contact-us", { params });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getContactUs = createAsyncThunk(
  "contactUs/get",
  async (contactId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/admin/contact-us/${contactId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateContactUsStatus = createAsyncThunk<
  { id: string; is_notified: boolean },
  { id: string; is_notified: boolean }
>(
  "contactUs/updateStatus",
  async ({ id, is_notified }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/admin/contact-us/${id}`, {
        is_notified,
      });
      return { id, is_notified };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateContactUs = createAsyncThunk(
  "contactUs/updateFull",
  async (
    { id, data }: { id: string; data: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.patch(`/admin/contact-us/${id}`, data);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteContactUs = createAsyncThunk(
  "contactUs/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/admin/contact-us/${id}`);
      return { id, data: response?.data ?? response };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

interface ContactState {
  isLoading: boolean;
  formLoader: boolean;
  message: any;
  allMessages: any;
  error: any;
}

const initialState: ContactState = {
  isLoading: false,
  message: null,
  formLoader: false,
  allMessages: [],
  error: null,
};

const ContactUsSlice: any = createSlice({
  name: "award",
  initialState,
  reducers: { resetState: () => initialState },
  extraReducers: (builder) => {
    builder
      .addCase(addContactUs.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addContactUs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload?.data?.data;
      })
      .addCase(addContactUs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(getAllContactUs.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllContactUs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allMessages = action.payload?.data;
      })
      .addCase(getAllContactUs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(getContactUs.pending, (state) => {
        state.isLoading = true;
        state.message = null;
      })
      .addCase(getContactUs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload?.data;
      })
      .addCase(getContactUs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(updateContactUsStatus.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(updateContactUsStatus.fulfilled, (state, action) => {
        state.formLoader = false;
        state.allMessages.data = state.allMessages.data.map(
          (message: { contact_us_contact_id: string }) =>
            message.contact_us_contact_id === action.payload.id
              ? { ...message, contact_us_is_notified: action.payload.is_notified }
              : message
        );
      })

      .addCase(updateContactUsStatus.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload;
      })
      .addCase(updateContactUs.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(updateContactUs.fulfilled, (state) => {
        state.formLoader = false;
      })
      .addCase(updateContactUs.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload;
      })
      .addCase(deleteContactUs.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(deleteContactUs.fulfilled, (state) => {
        state.formLoader = false;
      })
      .addCase(deleteContactUs.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload;
      });
  },
});

export const { resetState } = ContactUsSlice.actions;
export default ContactUsSlice.reducer;
